import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import {
  EmployeeAdvance,
  EmployeeAdvanceCreateCommand,
  EmployeeAdvanceSearchCommand,
  EmployeeAdvanceUpdate,
} from '../models/employee-advance/employee-advance';
import { ExpenseReceiptOwner } from '../models/expense-receipt/expense-receipt';
import { FundingBankAccount } from '../models/fund-movement/fund-movement';
import { PagedResult } from './bank-account.proxy.service';

/** Employee advances (Anticipo de viaje, #5789). */
@Injectable({ providedIn: 'root' })
export class EmployeeAdvanceProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/employee-advance';

  public search(
    command: EmployeeAdvanceSearchCommand,
    pageNumber: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: number,
  ): Observable<ApiResponse<PagedResult<EmployeeAdvance>>> {
    let params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize).set('sortOrder', sortOrder);
    if (sortField) params = params.set('sortField', sortField);
    return this.httpClient.post<ApiResponse<PagedResult<EmployeeAdvance>>>(`${this.baseUrl}/search`, command, { params });
  }

  /** The Open trips an advance can be opened for. */
  public getTrips(): Observable<ApiResponse<ExpenseReceiptOwner[]>> {
    return this.httpClient.get<ApiResponse<ExpenseReceiptOwner[]>>(`${this.baseUrl}/trips`);
  }

  public getById(id: string): Observable<ApiResponse<EmployeeAdvance>> {
    return this.httpClient.get<ApiResponse<EmployeeAdvance>>(`${this.baseUrl}/${id}`);
  }

  public getBankAccounts(id: string): Observable<ApiResponse<FundingBankAccount[]>> {
    return this.httpClient.get<ApiResponse<FundingBankAccount[]>>(`${this.baseUrl}/${id}/bank-accounts`);
  }

  public create(command: EmployeeAdvanceCreateCommand): Observable<ApiResponse<EmployeeAdvance>> {
    return this.httpClient.post<ApiResponse<EmployeeAdvance>>(this.baseUrl, command);
  }

  public update(id: string, update: EmployeeAdvanceUpdate): Observable<ApiResponse<EmployeeAdvance>> {
    return this.httpClient.put<ApiResponse<EmployeeAdvance>>(`${this.baseUrl}/${id}`, update);
  }

  public post(id: string): Observable<ApiResponse<EmployeeAdvance>> {
    return this.httpClient.post<ApiResponse<EmployeeAdvance>>(`${this.baseUrl}/${id}/post`, {});
  }

  public addAttachment(id: string, file: File): Observable<ApiResponse<EmployeeAdvance>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post<ApiResponse<EmployeeAdvance>>(`${this.baseUrl}/${id}/attachment`, formData);
  }

  public delete(id: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}/${id}`);
  }
}
